/**
 * Minimal client-side PDF (no dependency). Storage / e-invoicing come later.
 * Helvetica + Latin-1 text only.
 */

export interface InvoicePdfLineItem {
  description: string;
  details?: string;
  quantityLabel: string;
  unitPriceLabel: string;
  vatLabel?: string;
  totalLabel: string;
}

export interface InvoicePdfData {
  invoiceNumber: string;
  invoiceName?: string;
  issuedAtLabel: string;
  fromName: string;
  fromCompany?: string;
  fromSiret?: string;
  fromVatNumber?: string;
  fromAddressLines?: string[];
  toName: string;
  toCompany?: string;
  toSiret?: string;
  toVatNumber?: string;
  toAddressLines?: string[];
  projectName: string;
  amountLabel: string;
  paymentDateLabel?: string;
  statusLabel: string;
  notes?: string;
  paymentTerms?: string;
  paymentMethodLabel?: string;
  accountHolder?: string;
  iban?: string;
  bic?: string;
  lineItems?: InvoicePdfLineItem[];
  labels: {
    title: string;
    from: string;
    to: string;
    project: string;
    amount: string;
    paymentDate: string;
    status: string;
    notes: string;
    paymentTerms: string;
    paymentSection: string;
    paymentMethod: string;
    accountHolder: string;
    ibanLabel: string;
    bicLabel: string;
    siret: string;
    vatNumber: string;
    item: string;
    qty: string;
    unitPrice: string;
    vatRate: string;
    total: string;
  };
}

function escapePdfText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/[^\x20-\x7E\u00A0-\u00FF]/g, '?');
}

function buildContentStream(data: InvoicePdfData): string {
  const L = data.labels;
  const lines: Array<{ text: string; size: number; x: number; y: number }> = [];
  let y = 780;

  const push = (text: string, size = 11, x = 50) => {
    lines.push({ text, size, x, y });
    y -= size + 6;
  };

  const heading = data.invoiceName?.trim() || data.invoiceNumber;
  push(heading, 18);
  if (data.invoiceName?.trim() && data.invoiceNumber.trim()) {
    push(data.invoiceNumber.trim(), 11);
  }
  push(data.issuedAtLabel, 11);
  y -= 10;
  push(data.fromName || '—', 12);
  if (data.fromCompany) push(data.fromCompany);
  if (data.fromSiret) push(`${L.siret}: ${data.fromSiret}`);
  if (data.fromVatNumber) push(`${L.vatNumber}: ${data.fromVatNumber}`);
  for (const line of data.fromAddressLines || []) {
    if (line.trim()) push(line);
  }
  y -= 8;
  push(data.toName || '—', 12);
  if (data.toCompany) push(data.toCompany);
  if (data.toSiret) push(`${L.siret}: ${data.toSiret}`);
  if (data.toVatNumber) push(`${L.vatNumber}: ${data.toVatNumber}`);
  for (const line of data.toAddressLines || []) {
    if (line.trim()) push(line);
  }
  y -= 10;

  const items = data.lineItems?.length
    ? data.lineItems
    : [
        {
          description: data.projectName || '—',
          quantityLabel: '1',
          unitPriceLabel: data.amountLabel,
          totalLabel: data.amountLabel,
        },
      ];

  push(
    items.some((item) => item.vatLabel)
      ? `${L.item} / ${L.qty} / ${L.unitPrice} / ${L.vatRate} / ${L.total}`
      : `${L.item} / ${L.qty} / ${L.unitPrice} / ${L.total}`,
    10
  );
  for (const item of items.slice(0, 20)) {
    const vatPart = item.vatLabel ? ` | ${item.vatLabel}` : '';
    push(
      `${item.description} | ${item.quantityLabel} x ${item.unitPriceLabel}${vatPart} = ${item.totalLabel}`,
      10
    );
    if (item.details?.trim()) push(item.details.trim(), 9);
  }
  y -= 6;
  push(`${L.total}: ${data.amountLabel}`, 13);
  if (data.paymentDateLabel) push(`${L.paymentDate}: ${data.paymentDateLabel}`);
  push(`${L.status}: ${data.statusLabel}`);
  if (data.paymentTerms?.trim()) {
    y -= 8;
    push(`${L.paymentTerms}`, 12);
    const termLines = data.paymentTerms.trim().split(/\r?\n/).slice(0, 8);
    for (const line of termLines) push(line);
  }
  if (
    data.paymentMethodLabel ||
    data.accountHolder?.trim() ||
    data.iban?.trim() ||
    data.bic?.trim()
  ) {
    y -= 8;
    push(`${L.paymentSection}`, 12);
    if (data.paymentMethodLabel) push(`${L.paymentMethod}: ${data.paymentMethodLabel}`);
    if (data.accountHolder?.trim()) push(`${L.accountHolder}: ${data.accountHolder.trim()}`);
    if (data.iban?.trim()) push(`${L.ibanLabel}: ${data.iban.trim()}`);
    if (data.bic?.trim()) push(`${L.bicLabel}: ${data.bic.trim()}`);
  }
  if (data.notes?.trim()) {
    y -= 8;
    push(`${L.notes}`, 12);
    const noteLines = data.notes.trim().split(/\r?\n/).slice(0, 12);
    for (const line of noteLines) push(line);
  }

  const ops = lines
    .map((line) => {
      const safe = escapePdfText(line.text);
      return `BT /F1 ${line.size} Tf ${line.x} ${line.y} Td (${safe}) Tj ET`;
    })
    .join('\n');

  return ops;
}

function buildPdf(contentStream: string): Blob {
  const objects: string[] = [];
  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  objects.push('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');
  objects.push(
    '3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>\nendobj\n'
  );
  objects.push(
    `4 0 obj\n<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream\nendobj\n`
  );
  objects.push('5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n');

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [0];
  for (const obj of objects) {
    offsets.push(pdf.length);
    pdf += obj;
  }
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  for (let i = 1; i < offsets.length; i++) {
    pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
}

export function downloadInvoicePdf(data: InvoicePdfData, filename?: string): void {
  const blob = buildPdf(buildContentStream(data));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `${data.invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
